import { ReactNode } from 'react'

import clsx from 'clsx'

import { normalizeImage } from '@/lib/contentful/normalizers/image'

import { Image } from '@/components/Image'
import { Warning } from '@/components/Warning'

import { useResource } from '../../useResource'

type Props = {
	className?: string
	containerStyle?: string
	isHeaderActive?: boolean
	children?: ReactNode
}

export function WebinarSpeakers({ containerStyle, className, children, isHeaderActive }: Props) {
	const entry = useResource()

	if ('error' in entry) {
		return <Warning className={className}>{entry.error}</Warning>
	}

	const speakers = entry?.data?.speakersCollection?.items ?? []
	if (!speakers.length) {
		return null
	}

	return (
		<div className={clsx('flex flex-col items-center', containerStyle)}>
			{isHeaderActive && <>{children}</>}
			<div data-testid="webinar-speakers" className={clsx(className, 'grid')}>
				{speakers.map((item, idx) => {
					const name = item?.title
					const jobTitle = item?.jobTitle
					const description = item?.description
					const divider = !(idx === speakers.length - 1)
					const { url, alt = name } = normalizeImage(item?.image) ?? {}

					if (!name || !description || !url) return null

					return (
						<div key={name} data-testid="speaker-card">
							<div className="container grid md:grid-cols-2 gap-y-15 gap-x-7.5 items-center">
								<div className="grid gap-3">
									<h4 className="text-2xl font-medium leading-8 text-black-100">{name}</h4>
									<p className="text-sm text-gray-500 font-normal">{jobTitle}</p>
									<p className="text-[17px] leading-7 text-gray-700 font-normal">{description}</p>
								</div>

								<Image
									src={url}
									alt={alt ?? ''}
									height={204}
									width={548}
									className="max-md:row-start-1"
								/>
							</div>
							{divider && (
								<div className="py-[90px]">
									<hr className="border-[0px] border-b-[1px] border-gray-1000" />
								</div>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}
